import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { showToast } from "./toastSlice";
import getAPIbaseURL from "../../utils/helpers";
import i18n from "../../i18n";
const API_URL = `${getAPIbaseURL()}/users`;

const EMPTY_PERMISSIONS = {
  can_view_sensitive_users: false,
  can_update_users: false,
  can_create_users: false,
  can_delete_users: false,
};

export const changePassword = createAsyncThunk(
  "users/changePassword",
  async ({ current_password, new_password }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL}/change-password`,
        { current_password, new_password },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        }
      );
      return response.data;
    } catch (error) {
      thunkAPI.dispatch(
        showToast({
          message: error.response?.data?.detail || i18n.t("slices.userSlice.changePasswordError"),
          type: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
    }
  }
);

export const fetchUsers = createAsyncThunk("users/fetchUsers", async (options = {}, thunkAPI) => {
  try {
    const { includeInactive = false, includeDeleted = false, deletedOnly = false } = options || {};
    const params = {};
    if (includeInactive) params.include_inactive = true;
    if (includeDeleted) params.include_deleted = true;
    if (deletedOnly) params.deleted_only = true;
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API_URL}/`, {
      headers: { Authorization: `Bearer ${token}` },
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  } catch (error) {
    thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.fetchError"), type: "error" }));
    return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
  }
});

export const fetchManagers = createAsyncThunk(
  "users/fetchManagers",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/managers-and-admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Failed to fetch managers");
    }
  }
);

export const createUser = createAsyncThunk(
  "users/createUser",
  async (userData, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/`, userData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.createSuccess"), type: "success" }));
      return response.data;
    } catch (error) {
      thunkAPI.dispatch(
        showToast({
          message: error.response?.data?.detail || i18n.t("slices.userSlice.createError"),
          type: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
    }
  }
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (userId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.deleteSuccess"), type: "success" }));
      return userId;
    } catch (error) {
      thunkAPI.dispatch(
        showToast({
          message: error.response?.data?.detail || i18n.t("slices.userSlice.deleteError"),
          type: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
    }
  }
);

export const restoreUser = createAsyncThunk(
  "users/restoreUser",
  async (userId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/${userId}/restore`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.restoreSuccess"), type: "success" }));
      return response.data;
    } catch (error) {
      thunkAPI.dispatch(
        showToast({
          message: error.response?.data?.detail || i18n.t("slices.userSlice.restoreError"),
          type: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
    }
  }
);

export const toggleUserStatus = createAsyncThunk(
  "users/toggleUserStatus",
  async ({ userId, status }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL}/${userId}/status`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { status },
        }
      );
      thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.statusUpdateSuccess"), type: "success" }));
      return response.data.user;
    } catch (error) {
      thunkAPI.dispatch(
        showToast({
          message: error.response?.data?.detail || i18n.t("slices.userSlice.statusUpdateError"),
          type: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
    }
  }
);

export const changeUserRole = createAsyncThunk(
  "users/changeUserRole",
  async ({ userId, newRoleId }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const payload = { new_role_id: newRoleId };
      const response = await axios.put(
        `${API_URL}/${userId}/role`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.roleUpdateSuccess"), type: "success" }));
      return response.data.user;
    } catch (error) {
      thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.roleUpdateError"), type: "error" }));
      return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
    }
  }
);

export const fetchUsersByRole = createAsyncThunk("users/fetchUsersByRole", async (roleName, thunkAPI) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API_URL}/type/${roleName}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.fetchError"), type: "error" }));
    return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
  }
});

export const fetchUserById = createAsyncThunk(
  "users/fetchUserById",
  async (userId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.fetchError"), type: "error" }));
      return thunkAPI.rejectWithValue(error.response?.data || "Error fetching user");
    }
  }
);

export const fetchMyPermissions = createAsyncThunk(
  "users/fetchMyPermissions",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/me/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error fetching permissions");
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true;
      if (!localStorage.getItem("token")) return false;
      return getState()?.users?.permissionsLoaded !== true;
    },
  }
);

export const resetTeleworkLocation = createAsyncThunk(
  "users/resetTeleworkLocation",
  async (userId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API_URL}/${userId}/telework-location`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || null);
    }
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ userId, updates }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/${userId}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.updateSuccess"), type: "success" }));
      return response.data;
    } catch (error) {
      thunkAPI.dispatch(
        showToast({
          message: error.response?.data?.detail || i18n.t("slices.userSlice.updateError"),
          type: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
    }
  }
);

export const updatePreferences = createAsyncThunk(
  "users/updatePreferences",
  async (prefs, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/preferences`, prefs, {
        headers: { Authorization: `Bearer ${token}` },
      });
      thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.preferencesSuccess"), type: "success" }));
      return response.data;
    } catch (error) {
      thunkAPI.dispatch(
        showToast({
          message: error.response?.data?.detail || i18n.t("slices.userSlice.preferencesError"),
          type: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
    }
  }
);

export const fetchPreferences = createAsyncThunk(
  "users/fetchPreferences",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${getAPIbaseURL()}/users/me/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error fetching preferences");
    }
  }
);

export const fetchAbsenceApprovers = createAsyncThunk(
  "users/fetchAbsenceApprovers",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/absence-approvers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const uploadProfilePicture = createAsyncThunk(
  "users/uploadProfilePicture",
  async ({ file, userId = null }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const endpoint = userId 
        ? `${API_URL}/${userId}/upload-photo` 
        : `${API_URL}/me/upload-photo`;

      const response = await axios.post(
        endpoint,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.uploadPhotoSuccess"), type: "success" }));
      
      return { path: response.data.path, userId }; 
    } catch (error) {
      thunkAPI.dispatch(showToast({ message: i18n.t("slices.userSlice.uploadPhotoError"), type: "error" }));
      return thunkAPI.rejectWithValue(error.response?.data || "Error uploading profile picture");
    }
  }
);

export const deleteProfilePicture = createAsyncThunk(
  "users/deleteProfilePicture",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/me/photo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      thunkAPI.dispatch(
        showToast({
          message: i18n.t("slices.userSlice.pictureRemoved"),
          type: "success",
        })
      );
      return null;
    } catch (error) {
      thunkAPI.dispatch(
        showToast({
          message:
            error.response?.data?.detail || i18n.t("slices.userSlice.pictureRemoveError"),
          type: "error",
        })
      );
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);
export const getFormattedMember = (memberOrUser, users, roles) => {
  const user = users.find(u => u.id === (memberOrUser.id || memberOrUser.user_id)) || memberOrUser.user || memberOrUser;

  const firstName = user.first_name || "";
  const lastName = user.last_name || "";
  const fullName = (firstName || lastName)
    ? `${firstName} ${lastName}`.trim()
    : (user.name || user.username || "N/A");

  const roleObj = roles.find(r => r.id === (user.role_id || user.role?.id));
  const roleName = roleObj?.name || user.role?.name || user.role || i18n.t("components.editTeamModal.table.noRole");

  return {
    id: user.id,
    name: fullName,
    role: roleName,
    pic: user.profile_picture
      ? (user.profile_picture.startsWith("http") ? user.profile_picture : `${getAPIbaseURL()}${user.profile_picture}`)
      : null
  };
};

const userSlice = createSlice({
  name: "users",
  initialState: {
    items: [],
    currentUser: null,
    permissions: { ...EMPTY_PERMISSIONS },
    permissionsLoaded: false,
    isLoading: false,
    error: null,
    message: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.message = "User created successfully.";
      })
      .addCase(createUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.items = state.items.filter((user) => user.id !== action.payload);
        state.message = "User deleted successfully.";
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        const index = state.items.findIndex((user) => user.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.message = "User status updated successfully.";
      })
      .addCase(toggleUserStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(restoreUser.fulfilled, (state, action) => {
        state.items = state.items.filter((user) => user.id !== action.payload.id);
        state.message = "User restored successfully.";
      })
      .addCase(restoreUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(changeUserRole.fulfilled, (state, action) => {
        const index = state.items.findIndex((user) => user.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentUser && state.currentUser.id === action.payload.id) {
          state.permissionsLoaded = false;
        }
        state.message = "User role updated successfully.";
      })
      .addCase(changeUserRole.rejected, (state, action) => {
        const error = action.payload;
        if (error && Array.isArray(error.detail)) {
          state.error = error.detail[0].msg;
        } else {
          state.error = "Error updating user role";
        }
      })          
      .addCase(resetTeleworkLocation.fulfilled, (state, action) => {
        const index = state.items.findIndex((user) => user.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentUser && state.currentUser.id === action.payload.id) {
          state.currentUser = action.payload;
        }
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.items.findIndex((user) => user.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        
        if (state.currentUser && state.currentUser.id === action.payload.id) {
          state.currentUser = action.payload;
        }
        
        state.message = "User updated successfully.";
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload;
      })
            .addCase(fetchUserById.fulfilled, (state, action) => {
        state.currentUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchMyPermissions.fulfilled, (state, action) => {
        state.permissions = {
          ...state.permissions,
          ...action.payload,
        };
        state.permissionsLoaded = true;
      })
      .addCase(fetchMyPermissions.rejected, (state) => {
        state.permissions = { ...EMPTY_PERMISSIONS };
        state.permissionsLoaded = false;
      })
      .addCase(fetchUsersByRole.fulfilled, (state, action) => {
        state.items = action.payload;
      })
      .addCase(fetchUsersByRole.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updatePreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
        state.message = "Preferences updated.";
      })
      .addCase(updatePreferences.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
      })
      .addCase(uploadProfilePicture.fulfilled, (state, action) => {
        const { path, userId } = action.payload;

        if (state.currentUser && (!userId || state.currentUser.id === userId)) {
          state.currentUser.profile_picture = path;
        }
        if (userId) {
          const index = state.items.findIndex((user) => user.id === userId);
          if (index !== -1) {
            state.items[index].profile_picture = path;
          }
        }
        state.message = "Profile picture uploaded.";
      })  
      .addCase(deleteProfilePicture.fulfilled, (state) => {
        if (state.currentUser) {
          state.currentUser.profile_picture = null;
        }
        state.message = "Foto removida.";
      })
      .addMatcher(
        (action) =>
          action.type === "auth/logout" ||
          action.type === "auth/login/fulfilled" ||
          action.type === "auth/logoutUser/fulfilled",
        (state) => {
          state.permissions = { ...EMPTY_PERMISSIONS };
          state.permissionsLoaded = false;
        }
      );
  },
});

export const { clearError, clearMessage } = userSlice.actions;
export default userSlice.reducer;
